import { useEffect } from 'react';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { formatPrice } from '../utils/pricingUtils';

interface GroupPrice {
  minPrice: number; // Prezzo dell'addon piu' economico del gruppo
  count: number; // Quante varianti ha il gruppo (>1 => label "da ...")
}

interface GroupPriceMap {
  [addonGroup: string]: GroupPrice;
}

interface AddonGroupPriceState {
  groups: GroupPriceMap;
  loading: boolean;
  hasFetched: boolean;
  fetchGroups: () => Promise<void>;
  reset: () => void;
}

// Store Zustand: subscription_addons e' listino globale (nessun organization_id),
// quindi una fetch sola serve tutte le modali di tutta la sessione.
const useAddonGroupPriceStore = create<AddonGroupPriceState>((set, get) => ({
  groups: {},
  loading: false,
  hasFetched: false,

  fetchGroups: async () => {
    // Evita fetch multipli
    if (get().hasFetched || get().loading) {
      return;
    }

    set({ loading: true });

    try {
      // Solo gli addon ricorrenti: su un one_time (onboarding, bundle) la label
      // "/mese - disattivi quando vuoi" sarebbe falsa.
      const { data: addons, error } = await supabase
        .from('subscription_addons')
        .select('price, addon_group, addon_type')
        .eq('active', true)
        .eq('addon_type', 'recurring')
        .not('addon_group', 'is', null)
        .order('price', { ascending: true });

      if (error) {
        console.error('Error fetching addon prices:', error);
        set({ loading: false });
        return;
      }

      if (!addons || addons.length === 0) {
        set({ loading: false, hasFetched: true });
        return;
      }

      // La query e' gia' ordinata per prezzo crescente: il primo addon
      // incontrato per ogni gruppo e' il piu' economico.
      const groupMap: GroupPriceMap = {};

      addons.forEach((addon) => {
        const group = addon.addon_group as string;
        if (!groupMap[group]) {
          groupMap[group] = { minPrice: addon.price, count: 1 };
        } else {
          groupMap[group].count += 1;
        }
      });

      set({ groups: groupMap, loading: false, hasFetched: true });
    } catch (err) {
      console.error('Error in useAddonGroupPrice:', err);
      set({ loading: false });
    }
  },

  reset: () => set({ groups: {}, loading: false, hasFetched: false })
}));

/**
 * Prezzo di listino di un gruppo di add-on, gia' formattato per la UI.
 *
 * `addonGroup` e' il valore di `subscription_addons.addon_group` (vedi
 * `addonLockContent.ts`); passare `null` quando la modale e' chiusa evita
 * la fetch.
 *
 * La label vale `null` — e non una cifra di ripiego — quando il gruppo non
 * e' a listino, quando la query fallisce e mentre e' in volo. Sta sotto un
 * bottone che attiva un abbonamento a pagamento: una cifra sbagliata li' e'
 * peggio di nessuna cifra, e i consumatori la rendono dentro un `&&`.
 */
export function useAddonGroupPrice(addonGroup: string | null): {
  label: string | null;
  price: number | null;
  loading: boolean;
} {
  const { groups, loading, hasFetched, fetchGroups } = useAddonGroupPriceStore();

  useEffect(() => {
    if (addonGroup && !hasFetched && !loading) {
      fetchGroups();
    }
  }, [addonGroup, hasFetched, loading, fetchGroups]);

  const group = addonGroup ? groups[addonGroup] : undefined;

  if (!group) {
    return { label: null, price: null, loading };
  }

  // "59 €/mese + IVA" per un gruppo a taglia unica, "da 6 €/mese + IVA" per
  // uno a scaglioni: stessa forma delle etichette in lockedFeaturesRegistry.ts.
  const prefix = group.count > 1 ? 'da ' : '';

  return {
    label: `${prefix}${formatPrice(group.minPrice)}/mese + IVA`,
    price: group.minPrice,
    loading,
  };
}
